import React, { useEffect, useState } from 'react';
import { Alert, Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PdfPickerField from '../common/PdfPickerField';
import PrimaryActionButton from '../common/PrimaryActionButton';
import ScrollableDialog from '../common/ScrollableDialog';
import SwitchField from '../common/SwitchField';
import { validateConsentFormFile } from './consentFormValidation';
import { getConsentFormName } from './consentFormDisplay';

/** Uploads a new PDF version. Earlier versions are never replaced: they stay as evidence. */
const ConsentFormUploadDialog = ({ open, consentForm, saving, onClose, onUpload }) => {
  const { t, i18n } = useTranslation();
  const [file, setFile] = useState(null);
  const [requiresReacceptance, setRequiresReacceptance] = useState(false);
  const [error, setError] = useState(null);
  const isFirstVersion = !consentForm?.currentVersion;

  useEffect(() => {
    if (open) {
      setFile(null);
      setRequiresReacceptance(false);
      setError(null);
    }
  }, [open]);

  const handleSubmit = () => {
    const fileError = validateConsentFormFile(file);
    setError(fileError);
    if (!fileError) {
      onUpload(file, isFirstVersion || requiresReacceptance);
    }
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      title={t('consentForms.management.upload.title')}
      subtitle={consentForm ? getConsentFormName(consentForm.name, i18n.language, t) : null}
      dividers
      showCloseButton
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{ flexShrink: 0, justifyContent: 'space-between' }}
      actions={(
        <>
          <Button id="consent-form-upload-cancel" onClick={onClose} disabled={saving} color="inherit">
            {t('common.cancel')}
          </Button>
          <PrimaryActionButton id="consent-form-upload-submit" onClick={handleSubmit} disabled={saving}>
            {saving ? t('common.saving') : t('consentForms.management.upload.submit')}
          </PrimaryActionButton>
        </>
      )}
    >
      <Box id="consent-form-upload" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <PdfPickerField
          id="consent-form-upload-file"
          file={file}
          onChange={(nextFile) => {
            setFile(nextFile);
            setError(null);
          }}
          error={error}
          chooseLabel={t('consentForms.management.upload.choose')}
          replaceLabel={t('consentForms.management.upload.replace')}
          disabled={saving}
        />
        {isFirstVersion ? (
          <Alert severity="info">{t('consentForms.management.upload.firstVersionNote')}</Alert>
        ) : (
          <SwitchField
            id="consent-form-upload-reaccept"
            checked={requiresReacceptance}
            onChange={(event) => setRequiresReacceptance(event.target.checked)}
            label={t('consentForms.management.upload.requiresReacceptance')}
            helpText={t('consentForms.management.upload.requiresReacceptanceHelp')}
            disabled={saving}
            formControlLabelSx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { lineHeight: 1.4, pt: 0.5 } }}
          />
        )}
      </Box>
    </ScrollableDialog>
  );
};

export default ConsentFormUploadDialog;
